// #region color validation
type RgbTuple = [number, number, number];
type WCAGLevel = 'AA' | 'AAA';

export const VALIDATION_STATUS = {
  PASS: 'pass',
  FAIL: 'fail',
  ERROR: 'error',
} as const;

interface TextStyle {
  fontSize: number;
  fontWeight: number;
}

interface ValidationColorContrastArgs {
  customHexColor?: string;
  fixedHexColor?: string;
  fontStyles?: TextStyle[]; // Accept multiple text sizes & bold states
}

export interface ValidationContrastReturnType {
  status: (typeof VALIDATION_STATUS)[keyof typeof VALIDATION_STATUS];
  level: WCAGLevel;
  message: string | null;
}

/**
 * Used to simply check if the input color is a valid hex color.
 * @param hex - Hex color string (e.g. #fff, #ffffff).
 * @returns {boolean}
 */
export const isValidHex = (hex: string | undefined | null): boolean => {
  if (!hex || !Number.isNaN(Number(`0x${hex}`))) return false;
  return /^#([0-9A-Fa-f]{3}){1,2}$/.test(hex);
};

const hexToRgb = (hex: string | undefined | null): RgbTuple | null => {
  if (!hex || !isValidHex(hex)) {
    return null;
  }

  let copyHex = hex.replace(/^#/, '');

  if (copyHex.length === 3) {
    copyHex = copyHex
      .split('')
      .map(c => c + c)
      .join('');
  }

  const bigint = Number.parseInt(copyHex, 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
};

/**
 * WCAG COLOR CONTRAST
 */

/* 
  Contrast Ratio	WCAG Rating
  < 3.0	          ❌ Fails for all text
  3.0 - 4.49	    ⚠️ Passes for large text, but not normal text
  4.5+	          ✅ Passes for normal text
  7.0+	          🔥 Ideal for best accessibility
  Text Size	Bold?	  WCAG          Category
  12px      No      Normal Text   (4.5:1)
  14px	    No	    Normal Text   (4.5:1)
  14px	    Yes	    Large Text    (3.0:1)
  16px	    No	    Normal Text   (4.5:1)
  18px	    No	    Large Text    (3.0:1)
  24px	    No	    Large Text    (3.0:1)
*/

/**
 * @ref https://www.w3.org/WAI/GL/wiki/Relative_luminance
 */
const toLinear = (c: number) => {
  const color = c / 255;
  return color <= 0.03928 ? color / 12.92 : ((color + 0.055) / 1.055) ** 2.4;
};

const relativeLuminance = (rgb: [number, number, number] | null): number | null => {
  if (!rgb) return null;
  const [r, g, b] = rgb;
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

/**
 * @ref https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum#dfn-contrast-ratio
 * @ref https://www.accessibility-developer-guide.com/knowledge/colours-and-contrast/how-to-calculate/#the-formula
 * @param {number} lumiCustomColor the relative luminance of the custom color.
 * @param {number} lumiFixedColor the relative luminance of the fixed color (usually `white`).
 */
const getContrastRatio = (lumiCustomColor: number | null, lumiFixedColor: number | null): number =>
  (Math.max(lumiCustomColor ?? 0, lumiFixedColor ?? 0) + 0.05) /
  (Math.min(lumiCustomColor ?? 0, lumiFixedColor ?? 0) + 0.05);

/**
 * @ref https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum#dfn-large-scale
 */
const BOLD = 700;
const LARGE_FONT = 18;
const FONT_WITH_BOLD = 14;

export const WCAG_LEVEL = 'AA'; // Use only 'AA' for now.
const MIN_LEVEL_LARGE_TEXT_AA = 3.0; // 'AAA' -> 4.5
const MIN_LEVEL_NORMAL_TEXT_AA = 4.5; // 'AAA' -> 7.0

const isLargeScaleText = ({ fontSize, fontWeight }: TextStyle) =>
  fontSize >= LARGE_FONT || (fontSize >= FONT_WITH_BOLD && fontWeight >= BOLD);

const getRequiredRatio = (isLargeScaleFont: boolean) =>
  isLargeScaleFont ? MIN_LEVEL_LARGE_TEXT_AA : MIN_LEVEL_NORMAL_TEXT_AA;

const checkAllPass = (fontStyles: TextStyle[], userInputColorRatio: number) =>
  fontStyles.every(fontStyle => {
    const requiredRatio = getRequiredRatio(isLargeScaleText(fontStyle));
    return userInputColorRatio >= requiredRatio;
  });

/**
 * Validate color contrast based on WCAG (a11y) as well as acceptable hex value.
 * Usage: Custom color input for a button label, a background color, etc.
 * where two colors are used in the same element or place.
 */
export const validateColorContrast = ({
  customHexColor,
  fixedHexColor = '#fff',
  fontStyles = [{ fontSize: 18, fontWeight: 400 }],
}: ValidationColorContrastArgs) => {
  const lumiCustomColor = relativeLuminance(hexToRgb(customHexColor));
  const lumiFixedColor = relativeLuminance(hexToRgb(fixedHexColor));

  const validHex = typeof lumiCustomColor === 'number' && typeof lumiFixedColor === 'number';
  const userInputColorRatio = getContrastRatio(lumiCustomColor, lumiFixedColor);

  // Check if all font styles pass the min. contrast criterion.
  const hasAllPass = checkAllPass(fontStyles, userInputColorRatio);

  const defaultResponse = {
    level: WCAG_LEVEL,
    status: VALIDATION_STATUS.PASS,
  };

  if (!validHex) {
    // Hex value-validity (not WCAG-validity).
    return {
      ...defaultResponse,
      status: VALIDATION_STATUS.ERROR,
    };
  } else if (hasAllPass) {
    // WCAG-validity check: pass.
    return defaultResponse;
  }

  // WCAG-validity check: fail.
  return {
    ...defaultResponse,
    status: VALIDATION_STATUS.FAIL,
  };
};
// #endregion color validation
// #region hex color normalization
/**
 * Used in combination with normilizeColor to check if the hex color is valid.
 */
export const isHexColor = (hex: string) => hex.length === 6 && !Number.isNaN(Number(`0x${hex}`));
/**
 * Normalize hex color to 6 digits.
 * @param {string} hex - Hex color string (e.g. #fff, #ffffff).
 * @returns {string} - Normalized hex color string (e.g. #ffffff).
 */
export const normalizeColor = (hex: string) => {
  let normalized = hex.replace(/^#+/, '');
  if (normalized.length === 3) {
    normalized = normalized
      .split('')
      .map(c => c + c)
      .join('');
  }
  return normalized.toUpperCase();
};
// #endregion hex color normalization
